import type { Ref } from "react";
import { useEffect, useEffectEvent, useImperativeHandle, useRef, useState } from "react";
import { Provider } from "react-redux";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { logError } from "@entrilia/shared/logging";
import biApi from "../../../../../api/biApi";
import type {
  ConditionField,
  DimensionDescriptor,
  ReportField,
  TabularReportConfigurationForRunning,
  ValueCell,
} from "../../../../../api/types/svTypes";
import { ReportType } from "../../../../../api/types/svTypes";
import DrillDownEntry from "../../sv/components/builder/common/drillDown/DrillDownEntry";
import { formatFieldNumber } from "../../sv/components/builder/common/drillDown/utils/formulaPreviewHelper";
import useCommonReportHandlers from "../../sv/components/builder/common/hooks/useCommonReportHandlers";
import { BiCommonDataContextProvider } from "../../sv/contexts/BiCommonDataContextProvider";
import { useAppDispatch } from "../../sv/store/hooks/useAppDispatch";
import { selectCurrentReportId, selectRefreshToken } from "../../sv/store/selectors/currentReportSelectors";
import { store, useAppStore } from "../../sv/store/store";
import { changeDataSet } from "../../sv/store/thunks/metaDataThunks";
import { createCurrentReportAction, createDrillDownPageAction } from "../../sv/store/utils/storeUtils";
import { isAggregationBased, isCustomColumn } from "../../sv/utilities/fieldsHelper";
import type { SourceReportRequest } from "../report/sourceReportRequest";
import { toRunSourceRequest } from "../report/sourceReportRequest";
import { isExpectedRunFailure, removeErrorCode } from "./runErrorMessage";

export interface ArtifactLedgerRequest extends SourceReportRequest {
  reportId: string;
  fieldGuid: string;
  value: unknown;
  sessionId: string;
  etag: string;
  drillDownId: string;
}

export interface ArtifactLedgerDrillDownHandle {
  open: (request: ArtifactLedgerRequest) => void;
}

interface OpenedLedger {
  reportId: string;
  configuration: TabularReportConfigurationForRunning;
  field: ReportField;
  cell: ValueCell;
}

interface Props {
  ref?: Ref<ArtifactLedgerDrillDownHandle>;
  artifactId: string;
  onReload: () => void;
}

const noDimensions: DimensionDescriptor[] = [];
const noConditions: ConditionField[] = [];
const noNames: string[] = [];
const ignoreConditionUpdate = () => undefined;
const ignoreRefreshGrid = () => undefined;

const getRunConfiguration = withErrorHandling(biApi.smartViewArtifacts.getSourceReportRunConfiguration);

const LedgerWindow = ({ ledger }: { ledger: OpenedLedger }) => {
  const dispatch = useAppDispatch();
  const { onDrillDown } = useCommonReportHandlers(ledger.configuration.fields);
  const addLedgerPage = useEffectEvent(() => onDrillDown(ledger.field, ledger.cell, { reportId: ledger.reportId }));

  useEffect(() => {
    addLedgerPage();
    return () => {
      dispatch(createDrillDownPageAction("removeAll"));
    };
  }, [ledger, dispatch]);

  return (
    <BiCommonDataContextProvider
      reportType={ReportType.Tabular}
      books={ledger.configuration.books}
      isMultiBook={ledger.configuration.isMultiBook}
      dataSetId={ledger.configuration.dataSetId}
      dimensions={noDimensions}
      conditions={noConditions}
      dimensionNames={noNames}
      measureNames={noNames}
      editMode={false}
      reportId={ledger.reportId}
      refreshToken={undefined}
      onConditionUpdate={ignoreConditionUpdate}
      onRefreshGrid={ignoreRefreshGrid}
    >
      <DrillDownEntry reportId={ledger.reportId} />
    </BiCommonDataContextProvider>
  );
};

const LedgerDrillDown = ({ ref, artifactId, onReload }: Props) => {
  const dispatch = useAppDispatch();
  const appStore = useAppStore();
  const { sendNotificationError } = useNotificationContext();
  const [opened, setOpened] = useState<OpenedLedger>();
  const abortRef = useRef<AbortController>(undefined);
  const handleReload = useEffectEvent(onReload);

  useEffect(() => {
    appStore.dispatch(createCurrentReportAction("reset"));
    let refreshToken = selectRefreshToken(appStore.getState());
    const unsubscribe = appStore.subscribe(() => {
      const next = selectRefreshToken(appStore.getState());
      if (next === refreshToken) {
        return;
      }
      refreshToken = next;
      if (next) {
        handleReload();
      }
    });
    return () => {
      unsubscribe();
      abortRef.current?.abort();
      appStore.dispatch(createDrillDownPageAction("removeAll"));
      appStore.dispatch(createCurrentReportAction("reset"));
    };
  }, [appStore]);

  const open = async (request: ArtifactLedgerRequest) => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const [configuration, error] = await getRunConfiguration(
      artifactId,
      request.version,
      request.sourceKey,
      toRunSourceRequest(request),
      controller.signal
    );
    if (controller.signal.aborted) {
      return;
    }
    if (error) {
      if (error.isCanceledRequest) {
        return;
      }
      if (!isExpectedRunFailure(error.code, error.message)) {
        logError(error, "[ArtifactLedgerDrillDown] getSourceReportRunConfiguration");
      }
      sendNotificationError(removeErrorCode(error.message));
      return;
    }
    const field = configuration.fields.find((candidate) => candidate.config.guid === request.fieldGuid);
    if (!isAggregationBased(field) && !isCustomColumn(field)) {
      return;
    }
    const cell: ValueCell = {
      value: request.value,
      formattedValue: typeof request.value === "number" ? formatFieldNumber(request.value) : "",
      drillDownId: request.drillDownId,
    };
    const drillDownPageReportId = selectCurrentReportId(appStore.getState()) ?? request.reportId;
    setOpened({ reportId: drillDownPageReportId, configuration, field, cell });
    dispatch(changeDataSet(configuration.dataSetId));
    dispatch(createCurrentReportAction("setEtag", { etag: request.etag, cacheValid: undefined }));
    dispatch(createCurrentReportAction("setSessionId", request.sessionId));
  };

  useImperativeHandle(ref, () => ({ open }));

  return opened ? <LedgerWindow ledger={opened} /> : null;
};

const ArtifactLedgerDrillDown = (props: Props) => (
  <Provider store={store}>
    <LedgerDrillDown {...props} />
  </Provider>
);

export default ArtifactLedgerDrillDown;
